import { ETH_CHAIN_ID } from '@plug-core/constants/business'
import useConfig from '@plug-core/hooks/useConfig'
import { EVMChain } from '@plug-core/interfaces/common'
import { EthTransactionType, type ContractInfo, type GasData } from '@plug-core/interfaces/eth'
import { logger } from '@plug-core/logger'
import {
  callContractFunction,
  getERC20TokenMetadata,
  getEthTransactionInfo,
} from '@plug-core/utils/eth'
import { executeAndSetInterval } from '@plug-core/utils/executeAndSetInterval'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { getEthProvider } from '@plug-core/utils/getEthProvider'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import * as RadixPopover from '@radix-ui/react-popover'
import {
  Avatar,
  Banner,
  Heading,
  IconButton,
  Spinner,
  Surface,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from '@ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CiWarning } from 'react-icons/ci'

interface TokenData {
  decimals: number
  logo: string
  name: string
  symbol: string
  to: string
  value: bigint
}

// Same as MUI's `capitalize` (first letter only), which this file used before.
const capitalize = (value: string): string => {
  if (typeof value !== 'string') {
    throw new Error('`capitalize(string)` expects a string argument.')
  }
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const truncateEthAddress = (addr: string): string => {
  if (!addr || addr.length < 10) return addr
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`
}

const calculateTransactionFee = (
  gasLimit: bigint,
  maxFeePerGas: bigint,
  _priority: bigint
): bigint => gasLimit * maxFeePerGas

const getGasLimit = (gasData: GasData, type: EthTransactionType): bigint => {
  switch (type) {
    case EthTransactionType.APPROVE:
      return BigInt(gasData.erc20ApproveGas)
    case EthTransactionType.TRANSFER:
    case EthTransactionType.TRANSFER_FROM:
      return BigInt(gasData.erc20TransferGas)
    default:
      return BigInt(gasData.ethTransferGas)
  }
}

const fetchGasData = async (chain: EVMChain): Promise<GasData> => {
  const provider = getEthProvider(chain.rpcEndpoint)
  const fees = await provider.estimateFeesPerGas()
  return {
    maxFeePerGas: fees.maxFeePerGas.toString(),
    maxPriorityFeePerGas: (fees.maxPriorityFeePerGas ?? 0n).toString(),
    erc20ApproveGas: '65000',
    erc20TransferGas: '65000',
    ethDepositGas: '25000',
    ethTransferGas: '21000',
    erc20DepositGas: '80000',
  }
}

const buildTokenData = async (
  data: string | undefined,
  to: string,
  value: string | undefined,
  type: EthTransactionType,
  chain: EVMChain
): Promise<TokenData | null> => {
  try {
    const txInfo = getEthTransactionInfo(data)
    if (type === EthTransactionType.SEND_ETH) {
      return {
        decimals: 18,
        logo: '',
        name: 'Ethereum',
        symbol: 'ETH',
        to,
        value: BigInt(value || '0'),
      }
    }
    const metadata = await getERC20TokenMetadata(chain, to as `0x${string}`)
    return {
      decimals: metadata.decimals,
      logo: '',
      name: metadata.name,
      symbol: metadata.symbol,
      to: (txInfo.receiver ?? txInfo.spender ?? to) as string,
      value: txInfo.amount ?? 0n,
    }
  } catch {
    return null
  }
}

import { HiChevronDoubleRight, HiCodeBracket, HiLockOpen } from 'react-icons/hi2'

const FEE_REFRESH_INTERVAL = 10000

interface EthSendTransactionParams {
  transfer: {
    from?: string
    to: string
    value?: string
    data?: string
    gas?: string
    gasPrice?: string
    gasLimit?: string
    maxFeePerGas?: string
    maxPriorityFeePerGas?: string
    chainId?: string
  }
}

const TransferEth = ({ transfer }: EthSendTransactionParams) => {
  const [currentTab, setCurrentTab] = useState(0)
  const [gasCachedData, setGasCachedData] = useState<GasData>()
  const [tokenData, setTokenData] = useState<TokenData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [fee, setFee] = useState(0n)
  const [contractWarning, setContractWarning] = useState('')
  const [gasWarning, setGasWarning] = useState('')
  const [contractInformation, setContractInfomation] = useState<ContractInfo>()
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { evmChains } = useConfig()

  const ethChain = evmChains.find((chain) => chain.chainId === ETH_CHAIN_ID)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  let type = getEthTransactionInfo(transfer.data).type

  useEffect(() => {
    const fetchGasLimit = async () => {
      try {
        const fetchedContractInfo = await callContractFunction(
          transfer.to,
          transfer.data,
          transfer.value,
          transfer.from,
          transfer.gasLimit || transfer.gas,
          ethChain! // TODO: when fixing eth sign in, add dynamic chain
        )

        if (!contractInformation) {
          if (fetchedContractInfo) {
            setContractInfomation(fetchedContractInfo)
          } else if (type === EthTransactionType.CONTRACT_CALL) {
            setContractWarning(t('transferEth.warning'))
          }
        }

        if (
          (transfer.gas || transfer.gasLimit) &&
          transfer.maxFeePerGas &&
          transfer.maxPriorityFeePerGas
        ) {
          const calculatedFee = calculateTransactionFee(
            transfer.gas ? BigInt(transfer.gas) : BigInt(transfer.gasLimit || '0'),
            BigInt(transfer.maxFeePerGas),
            BigInt(transfer.maxPriorityFeePerGas)
          )
          setFee(calculatedFee)
        } else if (gasCachedData) {
          if (!fetchedContractInfo?.gasLimit && type === EthTransactionType.CONTRACT_CALL) {
            setGasWarning(t('transferEth.gasWarning'))
          }
          const calculatedFee = calculateTransactionFee(
            fetchedContractInfo?.gasLimit || getGasLimit(gasCachedData!, type),
            BigInt(gasCachedData.maxFeePerGas),
            BigInt(gasCachedData.maxPriorityFeePerGas)
          )
          setFee(calculatedFee)
        }
      } catch (error) {
        setContractWarning(t('transferEth.warning'))
        setFee(0n)
        logger.error('Error fetching gas', { originalError: error })
      }
    }

    fetchGasLimit()
  }, [gasCachedData, ethChain])

  useEffect(() => {
    if (
      (transfer.gas || transfer.gasLimit) &&
      transfer.maxFeePerGas &&
      transfer.maxPriorityFeePerGas
    ) {
      return
    }

    return executeAndSetInterval(async () => {
      setIsLoading(true)
      try {
        const data = await fetchGasData(ethChain!)
        setGasCachedData(data)
      } catch (error) {
        logger.error('Failed to get gas data', { originalError: error })
      } finally {
        setIsLoading(false)
      }
    }, FEE_REFRESH_INTERVAL)
  }, [transfer.gasLimit, transfer.gas, transfer.maxFeePerGas, transfer.maxPriorityFeePerGas])

  useEffect(() => {
    buildTokenData(transfer.data, transfer.to, transfer.value, type, ethChain!).then(setTokenData)
  }, [transfer.data, ethChain])

  if (!tokenData) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-4">
        <Spinner size={40} className="text-fg-2" />
        <Text variant="body-s" tone="fg-2">
          {t('transferEth.loading')}
        </Text>
      </div>
    )
  }

  const { decimals, logo, name, symbol, to, value } = tokenData

  const renderArrowView = () => {
    return (
      <span className="absolute grid place-items-center rounded-full bg-bg p-2 text-[30px] text-accent">
        {type === EthTransactionType.APPROVE ? (
          <HiLockOpen aria-hidden="true" />
        ) : type === EthTransactionType.CONTRACT_CALL ? (
          <HiCodeBracket aria-hidden="true" />
        ) : (
          <HiChevronDoubleRight aria-hidden="true" />
        )}
      </span>
    )
  }

  const renderPropertyRow = (title: string, value: string) => (
    <div className="flex flex-col gap-2">
      <Text variant="body-s" tone="fg-2">
        {title}
      </Text>
      <div className="min-h-5 rounded-sm bg-surface px-4 py-2">
        <Text variant="mono" tone="fg" breakAll>
          {value}
        </Text>
      </div>
    </div>
  )

  return (
    <Tabs
      value={String(currentTab)}
      onValueChange={(newValue) => setCurrentTab(Number(newValue))}
      className="flex-1"
    >
      <TabsList className="self-stretch">
        <TabsTrigger value="0" className="flex-1">
          {t('transferEth.info')}
        </TabsTrigger>
        <TabsTrigger value="1" className="flex-1">
          {t('transferEth.params.title')}
        </TabsTrigger>
      </TabsList>
      {contractWarning && (
        <Banner tone="warning" className="mt-2 mb-4">
          {contractWarning}
        </Banner>
      )}
      <TabsContent value="0" className="pb-4">
        <div className="flex flex-1 flex-col justify-between">
          <div className="relative mb-7 flex items-center justify-center">
            <Surface className="ml-2 flex flex-1 flex-col items-center gap-3">
              <Avatar src={logo} fallback={symbol} />
              <Text variant="caption" align="center">
                {symbol}
              </Text>
            </Surface>
            {renderArrowView()}
            <Surface className="mx-2 flex flex-1 flex-col items-center gap-3">
              <Avatar emoji="😇" />
              <Text variant="mono" align="center" className="text-[12px]">
                {truncateEthAddress(to)}
              </Text>
            </Surface>
          </div>

          <div className="flex flex-col gap-1 px-4">
            <div className="flex items-start justify-between gap-4 py-1.5">
              <Text weight="medium">{t('transferEth.params.transferAmount')}</Text>
              <Heading level={3} tabular align="end" className="text-[23px] text-info">
                {formatNumber(value, symbol)}
              </Heading>
            </div>
            <div className="flex items-center justify-between gap-4 py-1.5">
              <div className="flex items-center">
                <Text weight="medium">Fee</Text>
                {gasWarning && (
                  <RadixPopover.Root
                    open={!!anchorEl}
                    onOpenChange={(open) => {
                      if (!open) handleClose()
                    }}
                  >
                    <RadixPopover.Trigger asChild>
                      <IconButton
                        size="sm"
                        aria-label={t('transferEth.gasWarning')}
                        onClick={handleClick}
                        className="ml-1 size-6 text-danger"
                      >
                        <CiWarning />
                      </IconButton>
                    </RadixPopover.Trigger>
                    <RadixPopover.Portal>
                      <RadixPopover.Content
                        side="bottom"
                        align="start"
                        sideOffset={4}
                        className="plug-sheet-surface z-[1000] max-w-[300px] rounded-md border border-hairline bg-sheet p-4 text-fg shadow-md outline-none"
                      >
                        <Text>{gasWarning}</Text>
                      </RadixPopover.Content>
                    </RadixPopover.Portal>
                  </RadixPopover.Root>
                )}
              </div>
              <Text variant="body-s" tone="fg-2" tabular align="end">
                {isLoading ? t('transferEth.loading') : `${e8sToDecimals(fee, 18)} ETH`}
              </Text>
            </div>

            <div className="flex items-start justify-between gap-4 py-1.5">
              <Text weight="medium">Type</Text>
              <Text variant="body-s" tone="info" align="end">
                {capitalize(type)}
              </Text>
            </div>
          </div>
        </div>
      </TabsContent>
      <TabsContent value="1" className="pb-4">
        <div className="flex flex-col gap-2 px-4">
          {renderPropertyRow(t('transferEth.params.token'), name)}
          {type === EthTransactionType.SEND_ETH
            ? renderPropertyRow(t('transferEth.params.receiver'), truncateEthAddress(transfer.to))
            : renderPropertyRow(t('transferEth.params.sender'), truncateEthAddress(transfer.to))}
          {contractInformation?.name &&
            renderPropertyRow(t('transferEth.params.function'), contractInformation.name)}
          {contractInformation?.functionType &&
            renderPropertyRow(
              t('transferEth.params.functionType'),
              contractInformation.functionType
            )}
          {contractInformation?.inputs &&
            renderPropertyRow(
              t('transferEth.params.title'),
              contractInformation.inputs.map((input) => `${input.name}: ${input.type}`).join(', ')
            )}
          {transfer.data &&
            transfer.data.length > 2 &&
            renderPropertyRow(t('transferEth.params.hexData'), transfer.data)}
        </div>
      </TabsContent>
    </Tabs>
  )
}

export default TransferEth
